import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';

const GuideLongueurCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>Longueur CV : Combien de Pages en 2025 ? Guide Complet</title>
                <meta name="description" content="Combien de pages doit faire un CV en 2025 ? Règles selon l'expérience, par secteur, comment réduire ou enrichir. Guide complet avec exemples concrets." />
                <meta name="keywords" content="longueur CV, combien de pages CV, CV 1 page ou 2 pages, taille CV, longueur idéale CV, CV trop long" />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/longueur-cv" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment choisir la bonne longueur de CV",
                  "description": "Déterminez le nombre de pages idéal pour votre CV selon votre expérience et votre secteur.",
                  "step": [
                    {"@type": "HowToStep", "name": "Évaluer son niveau d'expérience", "text": "Moins de 3 ans : 1 page. 3 à 10 ans : 1 à 2 pages. Plus de 10 ans : 2 pages maximum."},
                    {"@type": "HowToStep", "name": "Supprimer les informations redondantes", "text": "Retirez les expériences de plus de 15 ans peu pertinentes et les compétences évidentes."},
                    {"@type": "HowToStep", "name": "Enrichir si le CV est trop court", "text": "Ajoutez des réalisations chiffrées, des projets personnels ou des formations."},
                    {"@type": "HowToStep", "name": "Adapter selon le secteur", "text": "Certains secteurs (académique, médical) acceptent des CV plus longs."},
                    {"@type": "HowToStep", "name": "Valider avec un outil d'analyse", "text": "Utilisez CV Analyse pour vérifier que votre CV est bien calibré."}
                  ]
                }
                `}</script>
            </Helmet>

            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600">
                        <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                        <li className="text-slate-400">/</li>
                        <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
                        <li className="text-slate-400">/</li>
                        <li className="text-white">Longueur CV</li>
                    </ol>
                </div>
            </nav>

            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">📏</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Longueur CV : Combien de Pages en 2025 ?
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Découvrez la longueur idéale de votre CV selon votre expérience et votre secteur d'activité
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="longueur-cv" />

            <article className="px-6 py-16 mx-auto max-w-5xl">
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">📋 Règle générale</h2>
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6">
                        <p className="text-xl text-gray-800 font-semibold mb-4">
                            <strong>1 page pour les débutants, 2 pages pour les expérimentés, 3 pages maximum pour les cadres dirigeants.</strong>
                        </p>
                        <p className="text-gray-700">
                            La longueur idéale dépend de votre niveau d'expérience, de votre secteur et du type de poste visé.
                        </p>
                    </div>
                </div>

                <div className="space-y-8 mb-8">
                    {[
                        { title: "CV 1 page", exp: "Débutants, étudiants, jeunes diplômés", content: "Idéal pour les profils avec moins de 5 ans d'expérience. Force la concision et la pertinence." },
                        { title: "CV 2 pages", exp: "Professionnels expérimentés (5-15 ans)", content: "Format standard pour la majorité des profils. Permet de détailler les expériences et compétences." },
                        { title: "CV 3 pages", exp: "Cadres dirigeants, experts (15+ ans)", content: "Acceptable uniquement pour les profils très expérimentés avec de nombreuses réalisations significatives." }
                    ].map((format, idx) => (
                        <div key={idx} className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">{format.title}</h2>
                            <div className="bg-indigo-50 rounded-xl p-4 mb-4">
                                <p className="font-semibold text-gray-900">👤 Pour : {format.exp}</p>
                            </div>
                            <p className="text-gray-700">{format.content}</p>
                        </div>
                    ))}
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">✂️ Comment réduire un CV trop long</h2>
                    <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start"><span className="text-indigo-600 mr-3">•</span>Supprimez les expériences anciennes ou non pertinentes</li>
                        <li className="flex items-start"><span className="text-indigo-600 mr-3">•</span>Résumez les descriptions en utilisant des verbes d'action</li>
                        <li className="flex items-start"><span className="text-indigo-600 mr-3">•</span>Retirez les formations obsolètes</li>
                        <li className="flex items-start"><span className="text-indigo-600 mr-3">•</span>Concentrez-vous sur les 5-7 dernières années</li>
                        <li className="flex items-start"><span className="text-indigo-600 mr-3">•</span>Éliminez les informations redondantes</li>
                    </ul>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">➕ Comment enrichir un CV trop court</h2>
                    <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start"><span className="text-purple-600 mr-3">•</span>Ajoutez des projets personnels ou académiques</li>
                        <li className="flex items-start"><span className="text-purple-600 mr-3">•</span>Détaillez vos compétences techniques et soft skills</li>
                        <li className="flex items-start"><span className="text-purple-600 mr-3">•</span>Mentionnez vos formations et certifications</li>
                        <li className="flex items-start"><span className="text-purple-600 mr-3">•</span>Ajoutez une section "Projets" ou "Réalisations"</li>
                        <li className="flex items-start"><span className="text-purple-600 mr-3">•</span>Développez votre profil professionnel</li>
                    </ul>
                </div>

                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-8 text-center text-white mb-16">
                    <h2 className="text-3xl font-bold mb-4">Votre CV est-il trop long, trop court ou parfaitement calibré ?</h2>
                    <p className="text-xl mb-6">Notre IA analyse la structure et la densité de votre CV pour maximiser l'impact — score et 3 conseils offerts</p>
                    <Link to="/" className="inline-block bg-white text-indigo-600 font-bold px-8 py-4 rounded-2xl hover:bg-gray-100 transition-colors">
                        Analyser mon CV gratuitement
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideLongueurCV;








